import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";

import RtlLayout from "layouts/rtl";
import AdminLayout from "layouts/admin";
import AuthLayout from "layouts/auth";
import { verifyToken } from "Services/allApi";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null); // null = loading, true/false = auth state
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem("access");
      
      if (!token) {
        setIsAuthenticated(false);
        return;
      }

      try {
        const response = await verifyToken({ token: token });
        console.log("Token verification response:", response);
        
        if (response && response.data.valid===true) {
          setIsAuthenticated(true);
        } else {
          // Token invalid, clear it
          localStorage.removeItem("access");
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error("Token verification failed:", error);
        localStorage.removeItem("access");
        setIsAuthenticated(false);
      }
    };

    checkAuth();
  }, []);

  // Show loading state while checking authentication
  if (isAuthenticated === null) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        Loading...
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/auth/sign-in" state={{ from: location }} replace />;
  }

  return children;
};

// Public Route Component (prevents authenticated users from accessing auth pages)
const PublicRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem("access");
      
      if (!token) {
        setIsAuthenticated(false);
        return;
      }

      try {
        const response = await verifyToken({ token: token });
        
        if (response && !response.error) {
          setIsAuthenticated(true);
        } else {
          localStorage.removeItem("access");
          setIsAuthenticated(false);
        }
      } catch (error) {
        localStorage.removeItem("access");
        setIsAuthenticated(false);
      }
    };

    checkAuth();
  }, []);

  if (isAuthenticated === null) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        Loading...
      </div>
    );
  }

  // If authenticated, redirect to admin (or the page they were trying to access)
  if (isAuthenticated) {
    const from = location.state?.from?.pathname || "/admin";
    return <Navigate to={from} replace />;
  }

  return children;
};

const App = () => {
  return (
    <Routes>
      {/* Public routes (auth pages) */}
      <Route 
        path="auth/*" 
        element={
          <PublicRoute>
            <AuthLayout />
          </PublicRoute>
        } 
      />
      
      {/* Protected routes */}
      <Route 
        path="admin/*" 
        element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="rtl/*" 
        element={
          <ProtectedRoute>
            <RtlLayout />
          </ProtectedRoute>
        } 
      />
      
      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/admin" replace />} />
    </Routes>
  );
};

export default App;